import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { X, Send, Trash2 } from "lucide-react";

const NotesPanel = ({ item, onClose }) => {
  const { api } = useAuth();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/content/${item.id}/notes`)
      .then((res) => setNotes(res.data))
      .finally(() => setLoading(false));
  }, [api, item.id]);

  const addNote = async () => {
    if (!text.trim()) return;
    try {
      const res = await api.post(`/content/${item.id}/notes`, { text: text.trim() });
      setNotes((prev) => [res.data, ...prev]);
      setText("");
    } catch {}
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
      <div
        className="relative h-full w-[420px] bg-[#0a0a0c] border-l border-white/[0.06] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideInRight 0.25s ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] shrink-0">
          <div>
            <h3 className="text-white font-semibold text-sm">Notes</h3>
            <p className="text-[#444] text-[11px] truncate max-w-[280px] mt-0.5">{item.title}</p>
          </div>
          <button onClick={onClose} className="text-[#555] hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Notes list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-white/10 border-t-white/50 rounded-full animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-[#333] text-sm text-center py-10">No notes yet. Add your first note below.</p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="group rounded-lg bg-white/[0.03] border border-white/[0.05] p-3.5">
                <p className="text-[#999] text-[13px] leading-relaxed">{note.text}</p>
                <div className="flex items-center justify-between mt-2.5">
                  <span className="text-[10px] text-[#333]">{new Date(note.created_at).toLocaleDateString()}</span>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="opacity-0 group-hover:opacity-100 text-[#444] hover:text-red-400/80 transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add note */}
        <div className="px-5 py-4 border-t border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()}
              placeholder="Write a note..."
              className="flex-1 bg-white/[0.03] border border-white/[0.07] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.15] transition-colors"
            />
            <button
              onClick={addNote}
              disabled={!text.trim()}
              className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.07] flex items-center justify-center text-[#555] hover:text-white hover:bg-white/[0.08] transition-all disabled:opacity-30"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesPanel;
